import {useEffect, useState} from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Box,
  Tag,
  Text,
  Link,
  HStack,
  Divider,
} from '@chakra-ui/react';
import {TablerBrandWindows, TablerEye, TablerEyeOff} from '../icons';
import {useColorModeValue} from '@chakra-ui/react';
import {useNavigate, useLocation} from 'react-router-dom';
import {analytics} from '../services/analytics';
import {getReadableAuthErrorMessage} from '../utils/authErrorMessages';

function Auth({
  login,
  resetPassword,
  loginWithMicrosoft,
  intendedPath,
}: Record<string, any>) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [messageTone, setMessageTone] = useState<'error' | 'success'>('error');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggingIn] = useState(false);
  const [lastLoginMethod, setLastLoginMethod] = useState<any>(null);

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const focusOutlineColor = useColorModeValue('opalite.600', 'opalite.300');
  const hoverBorderColor = useColorModeValue('gray.200', 'gray.700');
  const lastUsedTagBg = useColorModeValue('gray.500', 'gray.400');
  const lastUsedTagColor = useColorModeValue('white', 'gray.900');
  const linkColor = useColorModeValue('opalite.600', 'opalite.300');
  const linkHoverColor = useColorModeValue('opalite.700', 'opalite.200');
  const separatorTextColor = useColorModeValue('gray.300', 'gray.600');
  const subtleText = useColorModeValue('gray.600', 'gray.300');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const noticeStyles = {
    error: {
      bg: useColorModeValue('red.50', 'rgba(254, 178, 178, 0.12)'),
      borderColor: useColorModeValue('red.200', 'red.400'),
      color: useColorModeValue('red.700', 'red.200'),
    },
    success: {
      bg: useColorModeValue('green.50', 'rgba(154, 230, 180, 0.12)'),
      borderColor: useColorModeValue('green.200', 'green.400'),
      color: useColorModeValue('green.700', 'green.200'),
    },
  };
  const inputFocusStyles = {
    borderColor: focusOutlineColor,
    outline: '2px solid',
    outlineColor: focusOutlineColor,
    outlineOffset: '2px',
  };
  const fieldProps = {
    borderRadius: 'lg',
    borderColor,
    fontSize: 'md',
    h: '44px',
    minH: '44px',
    _hover: {borderColor: hoverBorderColor},
    _focusVisible: inputFocusStyles,
  };
  const labelProps = {
    color: textColor,
    fontSize: 'sm',
    fontWeight: 'normal',
  };

  // Read the last sign-in method so we can surface a "Last used" hint next
  // to the matching button. Stored by AuthContext after a successful login.
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const raw = window.localStorage.getItem('calliope:lastLoginMethod');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.method === 'string') {
        setLastLoginMethod(parsed.method);
      }
    } catch (e) {
      // Ignore malformed/legacy values.
    }
  }, []);

  async function handleSubmit(e: any, isLogin: any) {
    e.preventDefault();
    setError('');

    try {
      if (isLogin) {
        await login(email, password);

        // Navigate to the originally intended path or default to home
        const targetPath = intendedPath || location.state?.from || '/';
        console.log('Login successful - navigating to:', targetPath);
        navigate(targetPath);
      }
    } catch (err) {
      console.error('Caught error:', err);
      console.log('err.code:', err.code);
      console.log('err.message:', err.message);
      console.log('err.name:', err.name);
      console.log('typeof err:', typeof err);

      const readableError = getReadableAuthErrorMessage(err);
      setMessageTone('error');
      setError(readableError);
      analytics.capture('login error', {
        error_code: err.code,
        login_method: 'email',
      });
    }
  }

  async function handleLoginWithMicrosoft() {
    setError('');
    await loginWithMicrosoft().catch((error: any) => {
      console.error('Error in handleLoginWithMicrosoft:', error);
      setMessageTone('error');
      setError(getReadableAuthErrorMessage(error));
    });
  }

  async function handleResetPassword() {
    setError('');

    if (!email) {
      setMessageTone('error');
      setError('Please enter your email address to reset your password.');
      return;
    }

    try {
      await resetPassword(email);
      setMessageTone('success');
      setError('Password reset email sent. Please check your inbox.');
      analytics.capture('password reset requested');
    } catch (err) {
      console.error('Password reset error:', err);
      setMessageTone('error');
      setError(getReadableAuthErrorMessage(err));
    }
  }

  const handleTermsClick = () => {
    navigate('/docs/legal/terms-and-conditions');
  };

  const handlePrivacyClick = () => {
    navigate('/docs/legal/privacy-policy');
  };

  return (
    <Box as="form" onSubmit={(e: any) => handleSubmit(e, true)}>
      {error && (
        <Box
          mb={4}
          p={3}
          bg={noticeStyles[messageTone].bg}
          borderWidth="1px"
          borderColor={noticeStyles[messageTone].borderColor}
          borderRadius="md"
          role={messageTone === 'error' ? 'alert' : 'status'}
          aria-live={messageTone === 'error' ? 'assertive' : 'polite'}
        >
          <Text
            color={noticeStyles[messageTone].color}
            fontSize="sm"
            fontWeight="medium"
          >
            {error}
          </Text>
        </Box>
      )}

      <FormControl isRequired mb={4}>
        <FormLabel {...labelProps}>Email</FormLabel>
        <Input
          placeholder="email@company.com"
          autoComplete="email"
          onChange={e => setEmail(e.target.value)}
          {...fieldProps}
        />
      </FormControl>

      <FormControl isRequired mb={2}>
        <FormLabel {...labelProps}>Password</FormLabel>
        <InputGroup>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="***************"
            autoComplete="current-password"
            onChange={e => setPassword(e.target.value)}
            {...fieldProps}
          />
          <InputRightElement width="3rem" h="44px">
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={showPassword ? <TablerEyeOff /> : <TablerEye />}
              onClick={() => setShowPassword(!showPassword)}
              color={subtleText}
              borderRadius="full"
              _hover={{bg: 'transparent'}}
              _focusVisible={inputFocusStyles}
            />
          </InputRightElement>
        </InputGroup>
      </FormControl>

      <Box mb={4}>
        <Link
          as="button"
          type="button"
          color={linkColor}
          fontSize="sm"
          onClick={handleResetPassword}
          cursor="pointer"
          _hover={{textDecoration: 'underline', color: linkHoverColor}}
          _focus={{
            outline: 'none',
            boxShadow: '0 0 0 2px rgba(123, 123, 123, 0.2)',
          }}
          borderRadius="sm"
          p={1}
          ml={-1}
          background="transparent"
          border="none"
        >
          Forgot password?
        </Link>
      </Box>

      <Button
        type="submit"
        variant="opalitePrimary"
        w="100%"
        h="44px"
        minH="44px"
        px={6}
        fontSize="md"
        isLoading={isLoggingIn}
        loadingText="Signing in..."
      >
        Sign In
      </Button>

      <HStack spacing={4} align="center" mt={4} mb={4}>
        <Divider flex={1} />
        <Text fontSize="xs" color={separatorTextColor}>
          OR
        </Text>
        <Divider flex={1} />
      </HStack>

      <Box position="relative">
        {lastLoginMethod === 'microsoft' && (
          <Tag
            size="sm"
            position="absolute"
            top="-10px"
            right="12px"
            zIndex={2}
            bg={lastUsedTagBg}
            color={lastUsedTagColor}
            borderRadius="full"
            px={2}
            py={0.5}
            fontSize="xs"
            fontWeight="normal"
            letterSpacing="0.02em"
            boxShadow="0 2px 6px rgba(123, 123, 123, 0.35)"
            aria-label="You last signed in with Microsoft"
          >
            Last used
          </Tag>
        )}
        <Button
          type="button"
          w="100%"
          variant="outline"
          onClick={handleLoginWithMicrosoft}
          leftIcon={<TablerBrandWindows size={16} />}
          h="44px"
          minH="44px"
          px={{base: 3, sm: 4}}
          fontSize={{base: 'sm', sm: 'md'}}
        >
          Continue with Microsoft SSO
        </Button>
      </Box>

      <HStack
        as="nav"
        aria-label="Login help"
        spacing={4}
        justify="center"
        align="center"
        flexWrap="wrap"
        rowGap={2}
        mt={{base: 6, sm: 8, xl: '4em'}}
        position="relative"
        zIndex={10}
      >
        <Link
          as="button"
          type="button"
          onClick={handleTermsClick}
          color={linkColor}
          fontSize="sm"
          cursor="pointer"
          _hover={{textDecoration: 'underline', color: linkHoverColor}}
          _focus={{
            outline: 'none',
            boxShadow: '0 0 0 2px rgba(123, 123, 123, 0.2)',
          }}
          borderRadius="sm"
          p={1}
          background="transparent"
          border="none"
        >
          Terms
        </Link>
        <Text color="gray.500" fontSize="sm" aria-hidden="true">
          •
        </Text>
        <Link
          as="button"
          type="button"
          onClick={handlePrivacyClick}
          color={linkColor}
          fontSize="sm"
          cursor="pointer"
          _hover={{textDecoration: 'underline', color: linkHoverColor}}
          _focus={{
            outline: 'none',
            boxShadow: '0 0 0 2px rgba(123, 123, 123, 0.2)',
          }}
          borderRadius="sm"
          p={1}
          background="transparent"
          border="none"
        >
          Privacy
        </Link>
        <Text color="gray.500" fontSize="sm" aria-hidden="true">
          •
        </Text>
        <Link
          href="/support"
          color={linkColor}
          fontSize="sm"
          cursor="pointer"
          _hover={{textDecoration: 'underline', color: linkHoverColor}}
          _focus={{
            outline: 'none',
            boxShadow: '0 0 0 2px rgba(123, 123, 123, 0.2)',
          }}
          borderRadius="sm"
          p={1}
        >
          Support
        </Link>
      </HStack>
    </Box>
  );
}

export default Auth;
